import React from 'react'
import { Chip, Theme } from '@material-ui/core'
import { EntityClass, ResidentTermDirectoryStatus } from '../../../models'
import { FilterValue } from './FilterDrawer/types'
import { getEntityClassDisplayName } from '../../../models/DocumentAudit/services'

// Map status codes to display names
const statusDisplayNames: Record<ResidentTermDirectoryStatus, string> = {
    INITIAL: 'Initial',
    MATCHED: 'Matched',
    PARTIALLY_MATCHED: 'Mismatched',
    MISSING_RENT_ROLL: 'Missing Rent Roll',
    MISSING_LEASE: 'Missing Lease',
}

// Map time periods to display names
const timePeriodDisplayNames: Record<
    'current' | 'future' | 'past' | 'all',
    string
> = {
    current: 'Current Leases',
    future: 'Future Leases',
    past: 'Past Leases',
    all: 'All Leases',
}

interface FilterChipsProps {
    theme: Theme
    selectedFilters: Record<number, FilterValue>
    entityClassifications: EntityClass[]
    onFilterChange: (newFilters: Record<number, FilterValue>) => void
    hasMessagesFilter: boolean
    onHasMessagesFilterChange: (value: boolean) => void
    statusFilter: ResidentTermDirectoryStatus[]
    onStatusFilterChange: (statuses: ResidentTermDirectoryStatus[]) => void
    timePeriodFilter: 'current' | 'future' | 'past' | 'all'
    onTimePeriodFilterChange: (
        period: 'current' | 'future' | 'past' | 'all',
    ) => void
}

export const FilterChips: React.FC<FilterChipsProps> = ({
    theme,
    selectedFilters,
    entityClassifications,
    onFilterChange,
    hasMessagesFilter,
    onHasMessagesFilterChange,
    statusFilter,
    onStatusFilterChange,
    timePeriodFilter,
    onTimePeriodFilterChange,
}) => {
    // Handle removing an entity class filter
    const handleRemoveEntityFilter = (classificationId: number) => {
        const newFilters = { ...selectedFilters }
        newFilters[classificationId] = {
            ...newFilters[classificationId],
            enabled: false,
            min: null,
            max: null,
        }
        onFilterChange(newFilters)
    }

    // Handle removing a status filter
    const handleRemoveStatusFilter = (status: ResidentTermDirectoryStatus) => {
        const newStatuses = statusFilter.filter((s) => s !== status)
        onStatusFilterChange(newStatuses)
    }

    // Handle removing the messages filter
    const handleRemoveMessagesFilter = () => {
        onHasMessagesFilterChange(false)
    }

    // Handle changing the time period filter to default value
    const handleRemoveTimePeriodFilter = () => {
        onTimePeriodFilterChange('all')
    }

    // Get all active entity filters
    const activeEntityFilters = Object.entries(selectedFilters)
        .filter(([_, filter]) => filter.enabled)
        .map(([id, filter]) => {
            const classification = entityClassifications.find(
                (c) => c.id === Number(id),
            )
            return { id: Number(id), classification, filter }
        })
        .filter((item) => item.classification) // Make sure we found the classification

    // Is the time period filter not on default?
    const isTimePeriodFilterActive = timePeriodFilter !== 'all'

    // If there are no active filters, return null
    if (
        activeEntityFilters.length === 0 &&
        statusFilter.length === 0 &&
        !hasMessagesFilter &&
        !isTimePeriodFilterActive
    ) {
        return null
    }

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginTop: '8px',
                marginBottom: '8px',
            }}
        >
            {/* Time Period Filter Chip */}
            {isTimePeriodFilterActive && (
                <Chip
                    label={timePeriodDisplayNames[timePeriodFilter]}
                    onDelete={handleRemoveTimePeriodFilter}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
            )}

            {/* Status Filter Chips */}
            {statusFilter.map((status) => (
                <Chip
                    key={status}
                    label={statusDisplayNames[status]}
                    onDelete={() => handleRemoveStatusFilter(status)}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
            ))}

            {/* Messages Filter Chip */}
            {hasMessagesFilter && (
                <Chip
                    label="Has Messages"
                    onDelete={handleRemoveMessagesFilter}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
            )}

            {/* Entity Filter Chips */}
            {activeEntityFilters.map(({ id, classification, filter }) => {
                const displayName = getEntityClassDisplayName(
                    classification!.name,
                )
                let filterLabel = displayName

                // Add details based on filter type
                switch (classification!.data_type) {
                    case 'NUMBER':
                    case 'CURRENCY':
                        if (filter.min !== null && filter.max !== null) {
                            filterLabel = `${displayName}: ${filter.min} - ${filter.max}`
                        } else if (filter.min !== null) {
                            filterLabel = `${displayName}: ≥ ${filter.min}`
                        } else if (filter.max !== null) {
                            filterLabel = `${displayName}: ≤ ${filter.max}`
                        }
                        break
                    case 'DATE':
                        if (filter.min !== null && filter.max !== null) {
                            const fromDate = new Date(
                                String(filter.min),
                            ).toLocaleDateString()
                            const toDate = new Date(
                                String(filter.max),
                            ).toLocaleDateString()
                            filterLabel = `${displayName}: ${fromDate} - ${toDate}`
                        } else if (filter.min !== null) {
                            const fromDate = new Date(
                                String(filter.min),
                            ).toLocaleDateString()
                            filterLabel = `${displayName}: From ${fromDate}`
                        } else if (filter.max !== null) {
                            const toDate = new Date(
                                String(filter.max),
                            ).toLocaleDateString()
                            filterLabel = `${displayName}: Until ${toDate}`
                        }
                        break
                    case 'TEXT':
                        if (filter.min !== null) {
                            filterLabel = `${displayName}: "${filter.min}"`
                        }
                        break
                    case 'BOOLEAN':
                        if (filter.min !== null) {
                            filterLabel = `${displayName}: Yes`
                        }
                        break
                }

                return (
                    <Chip
                        key={id}
                        label={filterLabel}
                        onDelete={() => handleRemoveEntityFilter(id)}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                )
            })}
        </div>
    )
}
